import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useRef, useEffect } from 'react';
import { Folder, MoreVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface FolderHeaderEditableProps {
  folderName: string;
  videoCount: number;
  onRename: (newName: string) => void;
  onDuplicate?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  onClose: () => void;
}

export function FolderHeaderEditable({
  folderName,
  videoCount,
  onRename,
  onDuplicate,
  onMove,
  onDelete,
  onClose,
}: FolderHeaderEditableProps) {
  useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(folderName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setEditValue(folderName);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim() && editValue !== folderName) {
      onRename(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(folderName);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="bg-white border-b border-[#E4E4E7] px-8 py-5">
      <div className="flex items-center gap-4">
        {/* Folder Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#E8F1FF] flex items-center justify-center">
          <Folder className="h-6 w-6 text-[#007BFF]" />
        </div>

        {/* Folder Name (Editable) */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className={cn(
                "w-full text-lg font-semibold text-[#111827]",
                "bg-transparent border-b-2 border-[#7B61FF]",
                "focus:outline-none",
                "font-['Inter']"
              )}
            />
          ) : (
            <div className="flex items-center gap-3">
              <h2
                onClick={handleStartEdit}
                className={cn(
                  "text-lg font-semibold text-[#111827] cursor-pointer",
                  "hover:text-[#7B61FF] transition-colors",
                  "font-['Inter']"
                )}
              >
                {folderName}
              </h2>
              <span className="text-sm text-[#6B7280] font-medium">
                ({videoCount} {videoCount === 1 ? translateInterfaceText("vídeo") : translateInterfaceText("vídeos")})
              </span>
            </div>
          )}
        </div>

        {/* Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]"
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleStartEdit}>
              {translateInterfaceText("Renomear")}</DropdownMenuItem>
            {onDuplicate && (
              <DropdownMenuItem onClick={onDuplicate}>
                {translateInterfaceText("Duplicar pasta")}</DropdownMenuItem>
            )}
            {onMove && (
              <DropdownMenuItem onClick={onMove}>
                {translateInterfaceText("Mover pasta")}</DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onClick={onDelete}
                className="text-[#EF4444] data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
              >
                {translateInterfaceText("Excluir pasta")}</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-10 w-10 text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
